"use client";

import { toPlainDateString } from "@sa-nu/shared-utils/date";

import { useGtm } from "../../../../lib/google-tag-manager/gtm";
import { useRoomTypeReserveContext } from "../../room-type-reservation/_context/room-type-reserve-context";
import { PillButton } from "../../room-type-reservation/ui/pill-button";
import { usePendingStaySelectionContext } from "../_context/pending-stay-selection-context";
import { useSiteDetailDrawer } from "../site-detail-drawer-provider";

/**
 * 選択中の日程で拠点の全部屋タイプが予約不可、または絞り込みで部屋タイプが 0 件のときに、部屋タイプ一覧の代わりに表示する空き状況表示。
 * SiteDetailBody は必ず RoomTypeReserveProvider の子孫としてマウントされるため throw 版を使う
 * （site-detail-drawer.tsx の reserveCtx null ガードと同じ理由）。
 */
export function SiteDetailRoomsEmptyState({ siteSlug }: { siteSlug: string }) {
  const reserveCtx = useRoomTypeReserveContext();
  if (reserveCtx === null) {
    throw new Error(
      "SiteDetailRoomsEmptyState must be rendered within a RoomTypeReserveProvider",
    );
  }
  const pendingStayCtx = usePendingStaySelectionContext();
  const { close } = useSiteDetailDrawer();
  const gtm = useGtm();

  const handleSearchSameDates = () => {
    if (pendingStayCtx.firstNight === null) return;
    // 拠点一覧のセル選択で開いた拠点詳細の pendingStayCtx は、一覧側の確定済み
    // reserveCtx.firstNight/lastNight と一致するとは限らない。
    // 「同じ日程で探す」＝拠点詳細で見ていた日程のままにするため URL に反映する。
    reserveCtx.setStayRange(
      pendingStayCtx.firstNight,
      pendingStayCtx.lastNight,
    );
    reserveCtx.clearAreaFilter();
    // GTM は宿泊範囲をユーザー視点（CI〜CO）で送る方針のため、lastNight（最後に宿泊する夜）ではなく CO 日（+1日）を渡す。
    gtm.sendEvent("rt_rsv_detail_same_dates_clicked", {
      site_slug: siteSlug,
      range_start: toPlainDateString(pendingStayCtx.firstNight),
      range_end: toPlainDateString(pendingStayCtx.lastNight.add({ days: 1 })),
    });
    close();
  };

  return (
    <div className="flex flex-col items-start pt-[16px] pb-[12px]">
      <div className="flex items-start px-[16px]">
        <h3 className="dx-headline-2-mobile text-dx-text-placeholder">
          利用できるお部屋が見つかりません
        </h3>
      </div>
      <div className="flex items-start px-[16px]">
        <p className="dx-body-2 text-dx-text-placeholder">
          条件を変更してもう一度お試しください
        </p>
      </div>
      {/* 日程未選択でも EV 充電器などの絞り込みで部屋タイプが 0 件なら表示されるが、引き継ぐ日程が無いので導線は出さない */}
      {pendingStayCtx.firstNight !== null && (
        <div className="flex flex-col items-start px-[16px] pt-[8px]">
          <PillButton
            variant="ghost"
            size="medium"
            borderTone="muted"
            onClick={handleSearchSameDates}
          >
            同じ日程で別の拠点を探す
          </PillButton>
        </div>
      )}
    </div>
  );
}
